
import { connect } from 'react-redux';
import { useState, useRef, } from 'react';

import { siteboardAction } from '../actions/defaultActions';
import { SET_USER_ROLE_INFO_DIALOGUE_SHOWING, ADD_PROJECT_MEMBER, SET_ALERT, } from '../actions/types';
import { objectKeysToList, } from '../Util';

import MemberRolePicker from './MemberRolePicker';
import PushButton from './PushButton';

// icon imports
import { Plus, Send, Mail, X, Info, CircleCheck, Users, } from 'lucide-react';

const AddProjectMember = ({ 
  project, 
  onClose=null, 
  showBorder=true,
  _users, 
  _subscriptions, 
  siteboardAction, 
}) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [inviteSentConfirmation, setInviteSentConfirmation] = useState(false);
  const [userAddedConfirmation, setUserAddedConfirmation] = useState(false);

  const inputEmailRef = useRef(null);

  const suggestedEmailsMap = {};
  const projectMemberEmailsMap = {};
  const projectPendingMemberEmailsMap = {};
  const subscriptionMemberEmailsMap = {};
  try {
    for(const userId in _subscriptions[project.subscriptionId].members) {
      subscriptionMemberEmailsMap[_users[userId].email] = '';
    }
  } catch(error) {}
  for(const email in project.pendingMembers) {
    projectPendingMemberEmailsMap[email] = '';
  }
  for(const userId in project.members) {
    if(!(userId in _users)) continue;
    projectMemberEmailsMap[_users[userId].email] = '';
  }
  for(const userId in _users) {
    const email = _users[userId].email;
    if(!(email in projectMemberEmailsMap) && !(email in projectPendingMemberEmailsMap)) {
      suggestedEmailsMap[_users[userId].email] = '';
    }
  }
  const suggestedEmails = objectKeysToList(suggestedEmailsMap);
  const inputEmailIsSubscriptionMember = inputEmail.trim() in subscriptionMemberEmailsMap;
  const inputEmailAlreadyAdded = inputEmail.trim() in projectMemberEmailsMap;
  const inputEmailAlreadyInvited = inputEmail.trim() in projectPendingMemberEmailsMap;

  return (
    <div className={`
      relative flex flex-col gap-2
      rounded-xl 
      ${showBorder ? 'p-5 sm:p-6 text-white border border-white/20' : ''}
      shadow-[0_8px_28px_rgba(0,0,0,0.18)]
    `}>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-xl font-bold tracking-tight text-white">
          Invite a project member
        </h3>

        {/* Close button */}
        {typeof onClose === 'function' &&
          <button
            type="button"
            title="Close invitation form"
            aria-label="Close invitation form"
            onClick={onClose}
            className="
              group flex h-9 w-9 shrink-0 items-center justify-center
              rounded-full 
              hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.97] active:translate-y-0
              bg-[#f9a928] text-black
              focus-visible:outline-none
              focus-visible:ring-2 focus-visible:ring-[#8060ff]
            "
          >
            <X size={19} strokeWidth={2} />
          </button>
        }
      </div>

      {/* Form fields */}
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start">

        {/* Email */}
        <div className="min-w-0 flex-1">
          <label
            htmlFor="invite-member-email"
            className="
              mb-2 block text-sm font-semibold text-white/90
            "
          >
            Email Address
          </label>

          <div className="relative">
            <Mail
              size={17}
              strokeWidth={1.8}
              className="
                pointer-events-none absolute
                left-3.5 top-1/2 -translate-y-1/2
                text-[#9999a5]
              "
            />

            <input
              id="invite-member-email"
              ref={inputEmailRef}
              list="suggestedEmails"
              type="email"
              autoComplete="email"
              placeholder="colleague@company.com"
              value={inputEmail}
              onChange={(event) => {
                setInputEmail(event.target.value);
              }}
              className="project-detail-input pl-10"
            />
          </div>

          <datalist id="suggestedEmails">
            {suggestedEmails.map((email, index) => (
              <option value={email} key={index} />
            ))}
          </datalist>
        </div>

        {/* Role */}
        <div className="w-full sm:w-[220px] sm:shrink-0">
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              title="Learn about project roles"
              aria-label="Learn about project roles"
              onClick={() => {
                siteboardAction(
                  SET_USER_ROLE_INFO_DIALOGUE_SHOWING,
                  { isOpen: true }
                );
              }}
              className="
                flex items-center gap-1.5
                text-[#a7a7b0]
                transition-colors duration-200
                hover:text-[#b4a0ff]
                focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-[#8060ff]
              "
            >
              <span className='text-sm font-semibold text-white/90'>Project Role</span>
              <Info size={16} strokeWidth={1.9} />
            </button>
          </div>

          <div className="flex h-[2.7em] w-full items-center">
            <MemberRolePicker
              projectId={project.id}
              onChange={(role) => {
                setSelectedRole(role);
              }}
            />
          </div>
        </div>
      </div>

      {/* Submit button */}
      <div className='relative text-sm font-bold text-white'>
        <PushButton
          textColor="white"
          bgColor="#471cff"
          hoverBgColor="#603aff"
          disabled={
            inviteSentConfirmation ||
            userAddedConfirmation ||
            inputEmailAlreadyAdded ||
            inputEmailAlreadyInvited
          }
          onClick={() => {
            try {
              if (
                typeof inputEmail !== 'string' ||
                inputEmail.length === 0
              ) {
                throw new Error("Please enter the new member's email");
              }

              siteboardAction(
                ADD_PROJECT_MEMBER,
                {
                  projectId: project.id,
                  subscriptionId: project.subscriptionId,
                  email: inputEmail,
                  role: selectedRole,
                },
                () => {
                  if (inputEmailIsSubscriptionMember) {
                    setUserAddedConfirmation(true);
                    setTimeout(() => {
                      setUserAddedConfirmation(false);
                    }, 3000);
                  } else {
                    setInviteSentConfirmation(true);
                    setTimeout(() => {
                      setInviteSentConfirmation(false);
                    }, 3000);
                  }

                  setInputEmail('');
                }
              );
            } catch (error) {
              siteboardAction(SET_ALERT, {
                message: error.message,
              });
            }
          }}
        >
          {/* Default button content */}
          <div className="flex items-center justify-center gap-2">
            {inputEmailIsSubscriptionMember && !inputEmailAlreadyAdded ? (
              <>
                <Plus
                  size={19}
                  strokeWidth={2.2}
                  className="
                    transition-transform duration-200
                    group-hover:rotate-90
                    motion-reduce:transform-none
                  "
                />
                <span>Add to Project</span>
              </>
            ) : (
              <>
                <Send
                  size={18}
                  strokeWidth={2}
                  className="
                    transition-transform duration-200
                    group-hover:-translate-y-0.5
                    group-hover:translate-x-0.5
                    motion-reduce:transform-none
                  "
                />
                <span>Send Invitation</span>
              </>
            )}
          </div>
        </PushButton>

        {/* Confirmation overlay */}
        <div
          className={`
            absolute top-0 left-0 overflow-hidden
            w-full h-full
            pointer-events-none
          `}
        >
          <div
            className={`
              absolute top-0 left-0 inset-0
              w-full h-full
              flex items-center justify-center
              rounded-lg bg-[#059669]
              transition-[opacity,transform] duration-300
              ${
                inviteSentConfirmation ||
                userAddedConfirmation ||
                inputEmailAlreadyAdded ||
                inputEmailAlreadyInvited
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-full opacity-0'
              }
            `}
          >
            {inviteSentConfirmation && (
              <div className="flex items-center gap-2">
                <CircleCheck size={20} />
                <span>Invitation Sent</span>
              </div>
            )}

            {userAddedConfirmation && (
              <div className="flex items-center gap-2">
                <CircleCheck size={20} />
                <span>Added to Project</span>
              </div>
            )}

            {inputEmailAlreadyAdded && (
              <div className="flex items-center gap-2">
                <CircleCheck size={20} />
                <span>Already Added</span>
              </div>
            )}

            {inputEmailAlreadyInvited && (
              <div className="flex items-center gap-2">
                <CircleCheck size={20} />
                <span>Already Invited</span>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

// define redux state that will be passed in as props
const mapStateToProps = state => { return { 
  _users: state.serverSynced.users,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, {siteboardAction})(AddProjectMember);
